import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import CourseQuizzes from '@components/course-landing-page/quizzes'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import CodingalProductBlog from '@components/blog/product-blogs'
import { useState } from 'react'
import { formatSeoConfig } from '@lib/seo'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import ContentCard from '@components/course-landing-page/content-card'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { JAVASCRIPT } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['wd'].trialCourseQuery}`

export const JavaScriptSeo = formatSeoConfig({
  title: 'JavaScript for Kids: Live Online Coding Classes',
  description:
    'JavaScript for kids taught by expert instructors. Build websites, games and apps with a structured, K–12 aligned curriculum at Codingal. Try a free lesson.',
  url: 'https://www.codingal.com/courses/learn-javascript-for-kids-and-teens/',
  metaImage:
    'https://fastly.codingal.com/images/courses/og/codingal-dark-v1.jpg',
})

const BegineerGuide = () => (
  <>
    <h3 className="font-600 text-lg lg:text-xl">
      Why Should Kids Learn JavaScript?
    </h3>
    <p>
      JavaScript is one of the most valuable and exciting programming languages
      for kids to learn because it’s everywhere — from websites and mobile apps
      to games and even smart devices. It helps children understand how the
      digital world works while letting them create their own interactive
      projects.
    </p>

    <p>Here’s why learning JavaScript is great for kids 👇</p>
    <ul>
      <li>
        <strong>It powers the web:</strong> Nearly every modern website and app
        uses JavaScript. Learning it gives kids real-world skills that are both
        creative and practical.
      </li>
      <li>
        <strong>Builds problem-solving skills:</strong> Kids learn to think
        logically by breaking down challenges into small steps — just like real
        programmers.
      </li>
      <li>
        <strong>Encourages creativity:</strong> JavaScript lets children design
        games, animations, and interactive web pages, turning imagination into
        reality.
      </li>
      <li>
        <strong>Hands-on learning:</strong> Kids see instant results in their
        browser as they code, helping them understand cause and effect while
        staying motivated.
      </li>
      <li>
        <strong>Prepares for future careers:</strong> JavaScript is one of the
        most in-demand programming languages in the world, creating a great
        foundation for future tech and STEM paths.
      </li>
      <li>
        <strong>Works across platforms:</strong> Whether it’s web design, app
        development, or game creation, JavaScript is used everywhere, making it
        a versatile skill for young learners.
      </li>
    </ul>

    <p>
      By learning JavaScript, kids develop logical thinking, creativity, and
      confidence — gaining the skills to not just use technology, but to create
      it.
    </p>

    <h3 className="font-600 text-lg lg:text-xl">
      How Do You Explain JavaScript to Kids?
    </h3>
    <p>
      Imagine you’re telling your computer what to do — and it listens! 💻✨
      That’s what JavaScript does. It’s a special language that lets kids talk
      to computers to make websites move, buttons react, and games come alive.
    </p>
    <p>
      If <strong>HTML</strong> is like the body of a website and{' '}
      <strong>CSS</strong> is its clothes and colors, then{' '}
      <strong>JavaScript</strong> is the brain — it makes everything think,
      move, and respond.
    </p>

    <p>Here’s how kids can imagine it 👇</p>
    <ul>
      <li>
        🕹️ <strong>Like a Game Controller:</strong> When you press a button,
        something happens — JavaScript makes that happen on a website or app.
      </li>
      <li>
        🎨 <strong>Like Digital Art:</strong> You can make animations, drawings,
        or colorful effects that change with just a few lines of code.
      </li>
      <li>
        🤖 <strong>Like Giving Commands to a Robot:</strong> You tell the
        computer to “do this” or “change that,” and it follows your instructions
        exactly.
      </li>
    </ul>

    <p>Fun things kids can do with JavaScript:</p>
    <ul>
      <li>Create a bouncing ball animation 🏀</li>
      <li>Build a quiz game or score counter 🎮</li>
      <li>Make a button that changes color or plays music 🎵</li>
      <li>Design a mini website that reacts to clicks 🌈</li>
    </ul>

    <p>
      The best part? Kids get to see their code come to life instantly, which
      makes learning feel like magic! 🌟
    </p>
    <p>
      JavaScript helps kids understand how technology works — not just how to
      use it — turning them from tech users into young creators and
      problem-solvers. 🚀
    </p>

    <h3 className="font-600 text-lg lg:text-xl">
      What Does JavaScript Stand For?
    </h3>
    <p>
      Unlike some names that are acronyms, <strong>JavaScript</strong> doesn’t
      actually stand for anything — it’s the name of a programming language
      created to make web pages interactive and dynamic.
    </p>
    <p>
      It was developed in 1995 by <strong>Brendan Eich</strong> at{' '}
      <strong>Netscape</strong> to help websites do more than just show text and
      images. With JavaScript, pages could now respond to user actions, display
      animations, play sounds, and create fun, engaging experiences online.
    </p>

    <p>Here’s what the name means today 👇</p>
    <ul>
      <li>
        <strong>“Java”</strong> was added for marketing reasons because Java was
        a popular programming language at the time — but the two are completely
        different!
      </li>
      <li>
        <strong>“Script”</strong> refers to short pieces of code that make
        things happen, like a mini set of instructions for the browser.
      </li>
    </ul>

    <p>
      So, while the name “JavaScript” might sound like it’s related to Java,
      it’s actually its own unique language — simple, powerful, and made for the
      web.
    </p>
    <p>
      Today, JavaScript is the backbone of modern web development, used by
      companies like <strong>Google</strong>, <strong>YouTube</strong>, and{' '}
      <strong>Instagram</strong> — and it’s one of the most exciting languages
      for kids to start coding with!
    </p>

    <h3 className="font-600 text-lg lg:text-xl">
      An Easy Way to Understand JavaScript for Kids
    </h3>
    <p>
      The easiest way to teach JavaScript is through visual, hands-on learning
      that connects code with creativity.
    </p>

    <ul>
      <li>
        <strong>Start with a Story:</strong> Introduce JavaScript as a friendly
        character that follows commands.
      </li>
      <li>
        <strong>Show Before You Code:</strong> Demonstrate what the JavaScript
        will draw, like a square or star, before writing code.
      </li>
      <li>
        <strong>Teach Simple Commands:</strong> Begin with basics like{' '}
        <code>forward(100)</code> and <code>right(90)</code> to draw shapes.
      </li>
      <li>
        <strong>Use Loops for Patterns:</strong> Explain <code>for</code> loops
        to make repeated designs easily.
      </li>
      <li>
        <strong>Add Creativity:</strong> Let kids change colors, speeds, and
        shapes to make coding artistic.
      </li>
      <li>
        <strong>Give Mini-Challenges:</strong> Ask them to draw letters,
        spirals, or patterns for practice.
      </li>
    </ul>

    <p>
      JavaScript makes coding fun, visual, and beginner-friendly — helping kids
      learn programming logic while creating art with code.
    </p>
  </>
)

const Javascript = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...JavaScriptSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[JAVASCRIPT].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName="Learn Javascript with Top AI & Coding Course Instructors for Kids and Teens"
        description="Step into the fascinating world of JavaScript, where coding becomes a canvas for imagination and innovation! With Codingal’s interactive curriculum, students bring their ideas to life through animations, visuals, and interactive web projects  all created using simple lines of HTML, CSS, and JavaScript. In every session, learners use JavaScript to make web pages move, respond, and react — building creative designs, interactive stories, and dynamic effects. As they experiment with loops, functions, events, and logic, kids learn the fundamentals of programming in a fun, visual, and hands-on way. Each project introduces essential coding concepts such as variables, conditional statements, functions, and event handling, while encouraging creativity and problem-solving. From creating a Fireworks Animation or an Interactive Quiz to build a Mini Game, students learn how JavaScript powers the interactive web experiences they see every day. As students progress, they transition from visual coding platforms like Scratch and Play Lab to text-based environments such as Replit, p5.js, or CodePen. They learn how HTML and CSS work together with JavaScript to design and style complete web pages — preparing them for real-world web development. Perfect for kids aged 8–14, Codingal’s JavaScript lessons build a strong foundation in programming, boost creative confidence, and make learning code as exciting as playing a game! 🎮✨."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={JAVASCRIPT}
        urlQuery={urlQuery}
        isPlatformPage
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={JAVASCRIPT} />
          <CourseFeatures
            courseTitle={JAVASCRIPT}
            title={
              <Trans>An engaging JavaScript programming lessons for kids</Trans>
            }
          />
          <ClassCurriculum
            courseName={JAVASCRIPT}
            title="Our JavaScript Class Curriculum"
            description={
              <Trans>
                In Codingal’s Grandmaster AI & Coding program, students
                kickstart their JavaScript journey through fun, creative, and
                hands-on web development projects. They begin by learning the
                fundamentals of programming, syntax, variables, sequencing, and
                logic, while experimenting with HTML and CSS to design colorful
                and interactive web pages.
                <br />
                Each lesson helps students understand how code shapes the web,
                making programming simple, visual, and exciting. They explore
                key JavaScript concepts such as data types, loops, conditionals,
                and functions, applying them to create dynamic designs,
                animations, and logic-based interactions.
                <br />
                As students progress, they move into more advanced topics like
                arrays, methods, and call stacks, learning how to structure
                programs efficiently. They also explore classes, inheritance,
                array sorting, regular expressions, and JSON callbacks to manage
                data and build smarter, interactive projects.
                <br />
                Further along, students dive into asynchronous programming,
                APIs, and cookies, discovering how real websites connect, fetch
                data, and respond to users, just like professional developers
                do.
                <br />
                By the end of the course, kids will have a strong foundation in
                JavaScript, HTML, and CSS, capable of designing interactive
                games, animations, and functional web apps.
                <br />
                Perfect for Grades 4–8, this curriculum helps kids develop
                problem-solving skills, coding confidence, and creative
                thinking, preparing them for advanced coding, web development,
                and app design.
              </Trans>
            }
          />
          <Activities courseName={JAVASCRIPT} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked AI & coding
                tutors ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                Our instructors bring deep expertise in Computer Science and
                complete rigorous training before they teach. They’re truly
                amazing, and it shows in our exceptional Net Promoter Score
                (NPS) of 86! Students and parents consistently rate our teachers
                4.9 out of 5, and our curriculum earns a fantastic 4.8 out of 5.
                We’re so proud to have a faculty that’s 91% female and 9% male,
                creating an inclusive, encouraging learning environment where
                everyone feels welcome. Each tutor is trained to understand
                individual student needs and deliver personalized support, even
                in group settings. Plus, students have 24/7 access to expert
                tutors for instant doubt resolution, anytime they need help.
                This makes learning Javascript a truly supported experience,
                especially through our virtual Javascript lessons that connect
                kids with top educators worldwide.
              </Trans>
            }
          />
          <ContentCard
            title={
              <Trans>
                The Best Place for Kids to Learn the Basics and Fundamentals of
                Javascript
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  At Codingal, we provide the perfect environment for kids to
                  master JavaScript, the world’s most popular programming
                  language for creating interactive websites, animations, and
                  games. Our curriculum combines step-by-step lessons, creative
                  projects, and live guidance to help kids learn, build, and
                  explore coding with confidence. Whether it’s making buttons
                  react, building fun mini-games, or designing animations on web
                  pages, students develop logical thinking while expressing
                  their creativity through real code.
                </span>
              </Trans>
            }
            courseName={JAVASCRIPT}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                At Codingal, your child masters Javascript through a
                STEM.org–accredited program built on proven educational
                frameworks like Bloom’s Taxonomy, BIDE, and STEAM. Our
                curriculum is aligned with Common Core, state standards, AP
                Computer Science goals, and the K–12 Computer Science
                Framework—ensuring learning that’s both age-appropriate and
                college-ready. Through hands-on, project-based learning,
                students create real-world apps like temperature converters and
                scientific calculators, applying math, science, and engineering
                logic to solve everyday problems. Backed by STEM.org’s rigorous
                audits, every Javascript lesson lays a strong, future-proof
                foundation in STEM.
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your Javascript Certificate"
            description={
              <>
                In Codingal’s Grandmaster JavaScript module, students in grades
                1–3 begin with visual programming using Code.org’s Play Lab and
                Scratch, where they learn coding logic through storytelling,
                animations, and interactive games. As they progress to grades
                4–5, they transition into text-based JavaScript programming,
                exploring the fundamentals of web interactivity and creative
                coding.
                <br />
                Students learn how to write and execute real JavaScript code,
                work with HTML and CSS, and make web pages come alive using
                loops, events, functions, and variables. They also explore
                libraries like p5.js for drawing and animation, and build
                projects such as interactive quizzes, simple games, and dynamic
                web apps.
                <br />
                Upon successful completion, students earn the STEM.org-certified
                AI & Coding Grandmaster Certificate — a globally recognized
                credential that strengthens their academic portfolio, resumes,
                and college applications. With industry-relevant coding skills
                and a strong project portfolio, learners graduate with
                confidence, creativity, and a competitive edge for future
                studies and tech careers.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our Javascript classes are appropriate for elementary & middle school students"
            description={
              <>
                At Codingal, students begin their JavaScript journey by learning
                core programming ideas—commands, sequencing, events, and
                algorithms—while building playful, visual projects in
                kid-friendly environments. They first explore drag-and-drop
                logic (e.g., Scratch / Code.org) to grasp concepts like loops,
                conditionals, variables, and event handling. This builds
                confidence before they shift to text-based JavaScript for the
                web.
                <br />
                As they progress, learners move into real coding with HTML, CSS,
                and JavaScript, creating interactive pages and mini-apps. They
                practice DOM manipulation, event listeners, and functions, and
                work with arrays, objects, and loops to add logic and state.
                Projects include a Pac-Man–style maze, click-to-score games,
                interactive quizzes, and p5.js canvas art. Students also
                experiment with Text-to-Speech (Web Speech API), simple physics
                effects, and sound to enrich experiences—plus basic JSON and
                APIs for dynamic content.
                <br />
                These hands-on projects strengthen problem-solving and logical
                thinking while growing creativity through storytelling, game
                design, and interactive visuals. By the end, students can plan,
                code, test, and refine complete JavaScript projects—ready for
                deeper web development and app building.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['wd', 'cgm']} />
          <ContentCard
            urlQuery={urlQuery}
            title={<>The Ultimate Guide to Javascript for Kids</>}
            description={<BegineerGuide />}
          />
          <Faq courseName={JAVASCRIPT} title="Frequently Asked Questions" />
          <CourseQuizzes courseName={JAVASCRIPT} />
          <CodingalProductBlog urlQuery={urlQuery} isCoursePage />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={JAVASCRIPT}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="Javascript for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default Javascript
